import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listAllSlabs,
  createSlab,
  updateSlab,
  deleteSlab,
  listSlabsByProduct,
} from '../services/PriceSlabService';
import { listAllProductsSimple } from '../services/ProductService';
import { toast } from 'react-toastify';

const PriceSlabs = () => {
  const [slabs, setSlabs] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    productId: '',
    minQty: '',
    price: '',
  });

  const navigate = useNavigate();

  /* ---------------- LOAD DATA ---------------- */

  const loadSlabs = async () => {
    setLoading(true);
    try {
      const data = await listAllSlabs();
      setSlabs(data);
    } catch (err) {
      if ([401, 403].includes(err?.response?.status)) {
        navigate('/admin/login');
      } else {
        toast.error('Failed to load slabs');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const data = await listAllProductsSimple();
      setProducts(data);
    } catch {
      toast.error('Failed to load products');
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/admin/login');
      return;
    }
    loadProducts();
    loadSlabs();
  }, []);

  /* ---------------- FORM ---------------- */

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const resetForm = () => {
    setEditing(null);
    setForm({ productId: '', minQty: '', price: '' });
    loadSlabs();
  };

  /* ---------------- CRUD ---------------- */

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createSlab({
        productId: Number(form.productId),
        minQty: Number(form.minQty),
        price: Number(form.price),
      });
      toast.success('Slab created');
      resetForm();
    } catch {
      toast.error('Failed to create slab');
    }
  };

  const startEdit = (s) => {
    setEditing(s.id);
    setForm({
      productId: s.productId,
      minQty: s.minQty,
      price: s.price,
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateSlab(editing, {
        minQty: Number(form.minQty),
        price: Number(form.price),
      });
      toast.success('Slab updated');
      resetForm();
    } catch {
      toast.error('Failed to update slab');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this slab?')) return;
    try {
      await deleteSlab(id);
      toast.success('Deleted');
      loadSlabs();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const fetchForProduct = async () => {
    if (!form.productId) return toast.info('Select a product');
    try {
      const data = await listSlabsByProduct(Number(form.productId));
      setSlabs(data);
    } catch {
      toast.error('Failed to fetch slabs');
    }
  };

  /* ---------------- UI ---------------- */

  return (
  <div className="container py-4">
    {/* HEADER */}
    <div className="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h3 className="fw-bold mb-1">💰 Price Slabs</h3>
        <small className="text-muted">
          Manage quantity based pricing per product
        </small>
      </div>
      {editing && (
        <span className="badge bg-warning text-dark px-3 py-2">
          Editing Mode
        </span>
      )}
    </div>

    {/* FORM CARD */}
    <div className="card border-0 shadow-sm mb-4">
      <div className="card-body p-4">
        <form onSubmit={editing ? handleUpdate : handleCreate}>
          <div className="row g-4">

            {/* PRODUCT */}
            <div className="col-md-4">
              <label className="form-label fw-semibold">
                Product
              </label>
              <select
                name="productId"
                value={form.productId}
                onChange={handleChange}
                className="form-select form-select-lg"
                disabled={!!editing}
                required
              >
                <option value="">Select product</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} (ID #{p.id})
                  </option>
                ))}
              </select>
            </div>

            {/* MIN QTY */}
            <div className="col-md-2">
              <label className="form-label fw-semibold">
                Min Qty
              </label>
              <input
                type="number"
                min="1"
                name="minQty"
                value={form.minQty}
                onChange={handleChange}
                className="form-control form-control-lg"
                required
              />
            </div>

            {/* PRICE */}
            <div className="col-md-2">
              <label className="form-label fw-semibold">
                Price (₹)
              </label>
              <input
                type="number"
                step="0.01"
                name="price"
                value={form.price}
                onChange={handleChange}
                className="form-control form-control-lg"
                required
              />
            </div>

            {/* ACTIONS */}
            <div className="col-md-4 d-flex align-items-end gap-2">
              <button className="btn btn-primary btn-lg w-100" type="submit">
                {editing ? 'Update Slab' : 'Add Slab'}
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary btn-lg"
                onClick={fetchForProduct}
              >
                🔍
              </button>

              <button
                type="button"
                className="btn btn-outline-danger btn-lg"
                onClick={resetForm}
              >
                ✕
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    {/* TABLE CARD */}
    <div className="card border-0 shadow-sm">
      <div className="card-body p-0">

        {loading ? (
          <div className="text-center p-5 text-muted">
            Loading price slabs…
          </div>
        ) : slabs.length === 0 ? (
          <div className="text-center p-5 text-muted">
            No slabs found
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>Product</th>
                  <th>Min Qty</th>
                  <th>Price</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {slabs.map((s) => (
                  <tr key={s.id}>
                    <td className="fw-semibold">{s.id}</td>
                    <td>
                      <div className="fw-semibold">
                        {s.productName || 'Product'}
                      </div>
                      <small className="text-muted">
                        ID #{s.productId}
                      </small>
                    </td>
                    <td>
                      <span className="badge bg-info-subtle text-dark px-3 py-2">
                        {s.minQty}+
                      </span>
                    </td>
                    <td className="fw-bold">
                      ₹{Number(s.price).toFixed(2)}
                    </td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => startEdit(s)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(s.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  </div>
); 
};

export default PriceSlabs;
